import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';

const ENV_META = {
  prod: {
    label: 'Prod',
    color: 'success',
    description: 'api.app.comprehendpt.com',
  },
  dev: {
    label: 'Dev',
    color: 'warning',
    description: 'api.dev.comprehendpt.com',
  },
  local: {
    label: 'Local',
    color: 'info',
    description: 'http://127.0.0.1:5001',
  },
};

const BackendEnvironment = ({ brandText }) => {
  const { apiClient } = useContext(GlobalContext);
  const [currentEnv, setCurrentEnv] = useState(
    apiClient.getBackendEnv ? apiClient.getBackendEnv() : 'prod',
  );
  const [isDevUser, setIsDevUser] = useState(Boolean(apiClient?.isDev));
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);

  const closeSwitcher = useCallback(() => {
    setSwitcherOpen(false);
  }, []);

  useEffect(() => {
    const profileHandler = (client) => {
      setIsDevUser(Boolean(client?.isDev));
      if (client?.getBackendEnv) {
        setCurrentEnv(client.getBackendEnv());
      }
      if (!client?.isDev) {
        closeSwitcher();
      }
    };
    if (apiClient.onProfileUpdated) {
      apiClient.onProfileUpdated(profileHandler);
    }
    // Run once in case profile is already loaded
    profileHandler(apiClient);
    return () => {
      if (apiClient.offProfileUpdated) {
        apiClient.offProfileUpdated(profileHandler);
      }
    };
  }, [apiClient, closeSwitcher]);

  useEffect(() => {
    const backendHandler = (env) => setCurrentEnv(env);
    if (apiClient.onBackendUpdated) {
      apiClient.onBackendUpdated(backendHandler);
    }
    return () => {
      if (apiClient.offBackendUpdated) {
        apiClient.offBackendUpdated(backendHandler);
      }
    };
  }, [apiClient]);

  useEffect(() => {
    if (!switcherOpen) return;
    const handleClickOutside = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        closeSwitcher();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [switcherOpen, closeSwitcher]);

  const handleBrandClick = () => {};

  const handleContextMenu = (e) => {
    if (!isDevUser) return;
    e.preventDefault();
    e.stopPropagation();
    setSwitcherOpen((open) => !open);
  };

  const handleEnvChange = (envKey) => {
    if (!apiClient?.setBackendEnv) {
      return;
    }
    apiClient.setBackendEnv(envKey);
    closeSwitcher();
  };

  const envMeta = ENV_META[currentEnv] || ENV_META.prod;

  return (
    <div className="d-flex align-items-center position-relative">
      {isDevUser && (
        <div
          onContextMenu={handleContextMenu}
          title="Switch backend (dev)"
          style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '12px',
            height: '12px',
            borderRadius: '6px',
            border: '1px dashed rgba(255,255,255,0.5)',
            background: 'rgba(255,255,255,0.05)',
            cursor: 'context-menu',
            zIndex: 2,
          }}
          aria-label="Backend switcher (dev)"
        />
      )}
      <Link
        className="h4 mb-0 text-white text-uppercase d-none d-lg-inline-block"
        to="/"
        onClick={handleBrandClick}
        onContextMenu={handleContextMenu}
      >
        {brandText}
      </Link>

      {isDevUser && currentEnv !== 'prod' && (
        <Badge
          color={envMeta.color}
          pill
          className="ml-3"
          style={{ zIndex: 1 }}
        >
          Backend: {envMeta.label}
        </Badge>
      )}

      {switcherOpen && isDevUser && (
        <div
          className="bg-white border rounded shadow-sm p-3 position-absolute"
          ref={switcherRef}
          style={{ top: '2.6rem', left: 0, zIndex: 1050, minWidth: '260px' }}
        >
          <div className="text-uppercase text-muted small mb-2">
            Backend target
          </div>
          {Object.entries(ENV_META).map(([key, meta]) => {
            const isActive = key === currentEnv;
            return (
              <Button
                key={key}
                size="sm"
                color={isActive ? 'primary' : 'secondary'}
                outline={!isActive}
                className="mr-2 mb-2"
                onClick={() => handleEnvChange(key)}
              >
                {meta.label}
              </Button>
            );
          })}
          <hr className="my-2" />
          <Link
            to="/User/dev/mock-emr"
            className="small"
            onClick={closeSwitcher}
          >
            Mock EMR Dev Tools
          </Link>
          <div className="small text-muted mt-1">
            Visible to dev accounts, resets per tab.
            <br />
            {envMeta.description}
          </div>
        </div>
      )}
    </div>
  );
};

export default BackendEnvironment;
